// Follow the live graph: open /rail/events first and buffer, load the snapshot
// (GET /rail/graph), then apply the buffered messages and everything after.
// Messages at or below the snapshot's seq are dropped by the reducer, so
// nothing is lost or doubled between the two. On a drop, reconnect with
// exponential backoff (with jitter) and reload the snapshot to catch up.
// In replay mode the snapshot is skipped and the graph assembles from the
// replay's messages.
import type { LiveEvent } from "../api/events";
import type { RailGraph } from "./model";

export type StreamStatus = "connecting" | "live" | "reconnecting" | "closed";

export interface StreamDeps {
  url: string;                                        // ws(s)://…/rail-api/rail/events
  snapshot: () => Promise<RailGraph>;
  WebSocketImpl?: { new (url: string): WebSocket };
  random?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

export interface StreamHandlers {
  onSnapshot: (g: RailGraph) => void;
  onEvent: (e: LiveEvent) => void;
  onStatus: (s: StreamStatus, attempt: number) => void;
}

export const BACKOFF_BASE_MS = 1000;
export const BACKOFF_MAX_MS = 30_000;

/** The delay before reconnect attempt n (1-based): base·2^(n-1), capped, with ±20% jitter. */
export function backoff(attempt: number, random = Math.random): number {
  const d = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempt - 1));
  return Math.round(d * (0.8 + 0.4 * random()));
}

export function follow(deps: StreamDeps, h: StreamHandlers, opts: { replay?: boolean } = {}): () => void {
  const WS = deps.WebSocketImpl ?? WebSocket;
  const setT = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearT = deps.clearTimer ?? ((t) => clearTimeout(t as any));
  let attempt = 0, closed = false, ws: WebSocket | null = null, timer: unknown = null;

  const connect = () => {
    if (closed) return;
    h.onStatus(attempt ? "reconnecting" : "connecting", attempt);
    let buffer: LiveEvent[] | null = opts.replay ? null : [];
    const sock = new WS(deps.url);
    ws = sock;
    sock.onmessage = (m: MessageEvent) => {
      let e: LiveEvent;
      try { e = JSON.parse(String(m.data)); } catch { return; }
      if (buffer) buffer.push(e); else h.onEvent(e);
    };
    sock.onopen = () => {
      attempt = 0;
      if (opts.replay) { h.onStatus("live", 0); return; }
      deps.snapshot().then((g) => {
        if (closed || ws !== sock) return;
        h.onSnapshot(g);
        const pending = buffer ?? [];
        buffer = null;
        for (const e of pending) h.onEvent(e);
        h.onStatus("live", 0);
      }, () => sock.close());
    };
    sock.onclose = () => {
      if (closed || ws !== sock) return;
      attempt += 1;
      h.onStatus("reconnecting", attempt);
      timer = setT(connect, backoff(attempt, deps.random));
    };
    sock.onerror = () => { try { sock.close(); } catch { /* closing */ } };
  };

  connect();
  return () => { closed = true; if (timer) clearT(timer); ws?.close(); h.onStatus("closed", 0); };
}

export function eventsUrl(apiBase: string, loc: Location = location): string {
  const origin = loc.origin.replace(/^http/, "ws");
  return (apiBase.startsWith("http") ? apiBase.replace(/^http/, "ws") : origin + apiBase) + "/rail/events";
}
